import { useEffect, useRef, useState } from "react";

import { type CellRef, cellRef, getRelativeRow } from "./cell-ref";
import { useActivePageId, usePageProducts } from "../../data";
import { useGrid } from "./stores";
import { colParams } from "../utils";

export const useToggleCellSelection = () => {
  const [state, setState] = useState({ active: false });
  const [gridApi] = useGrid();
  const resolveRef = useRef(Promise.withResolvers<null | CellRef>());
  const cleanupRef = useRef((_: null | CellRef) => void null);
  const pageId = useActivePageId();
  const pageProducts = usePageProducts(pageId);

  useEffect(() => () => cleanupRef.current(null), []);

  // biome-ignore lint/suspicious/useAwait: we want to return a promise here
  const toggle = async () => {
    setState((prev) => ({ active: !prev.active }));
    if (state.active) {
      cleanupRef.current(null);
      cleanupRef.current = () => void null;
      return;
    }

    const original = gridApi?.getGridOption("onCellClicked");
    const root = document.querySelector(".ag-root-wrapper");
    if (!root) return console.error("ag-root-wrapper not found");
    root.classList.add("ag-cell-selection");

    cleanupRef.current = (s) => {
      root.classList.remove("ag-cell-selection");
      gridApi?.setGridOption("onCellClicked", original);
      resolveRef.current.resolve(s);
      setState((prev) => ({ ...prev, active: false }));
    };

    gridApi?.setGridOption("onCellClicked", (e) => {
      const columnId = colParams(e.column)?.gridId ?? e.column.getColId();
      const period = getRelativeRow(e.node); // Now returns absolute period
      const page = pageProducts.data?.find((y) => y.grid_id === columnId);
      if (!page || !page.product_id) return;
      if (!period) return;
      cleanupRef.current(
        cellRef({
          columnId,
          productId: page.product_id,
          fieldSelector: page.column_field_selector,
          period: period, // Now using absolute period
        }),
      );
      cleanupRef.current = () => void null;
    });
    resolveRef.current = Promise.withResolvers<null | CellRef>();

    return resolveRef.current.promise;
  };

  return [state, toggle] as const;
};
